import { CheckoutIframeWrapper, PurchaseWrapper } from "../../../shared/styles";
import {
  CustomLabel,
  FlexContainer,
  IStyleProps,
  Loader,
} from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { CheckOutStore } from "../../../shared/store";
import { CustomPlaceholders, NavPills } from "../../../shared/components";
import {
  IFRAMEMessageTypes,
  UpsertCheckoutActiveStep,
} from "../../../shared/enums";
import placeholderIcon from "../../../../../../images/placeholders/item-muted-grey.svg";

type PreviewMode = "desktop" | "mobile";

type IProps = {
  checkoutStore: CheckOutStore;
  action?: string | null;
};

export const IFrameWrapper = styled.div<IStyleProps>`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow-x: auto;
  overflow-y: hidden;
  max-width: ${(props) => props.maxWidth || "100%"};
  display: flex;
  justify-content: center;

  iframe {
    border: none;
    width: 100%;
    height: 100%;
    transform-origin: top left;
    margin: 0 auto;
  }

  .desktop {
    width: 1250px;
    /* height: 1000px; */
  }

  .mobile {
    width: 600px;
    /* height: 800px; */
  }
  .preview-wrappper {
    padding: 30px;
    ${PurchaseWrapper} {
      padding: 25px 30px !important;
    }
    .purchase-wrapper-donation-payment {
      padding: 25px 15px !important;
    }
  }
`;

const CheckoutIFrame: React.FC<IProps> = ({ checkoutStore, action }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // const [zoom, setZoom] = useState(100);
  const [previewMode, setPreviewMode] = useState<PreviewMode>(
    window.innerWidth > 1900 ? "desktop" : "mobile",
  );
  // const [height, setHeight] = useState(previewMode === 'mobile' ? 800 : 1000);
  const [width, setWidth] = useState(previewMode === "mobile" ? 600 : 1250);
  const [messagePostedRecieved, setMessagePostedRecieved] = useState(false);
  /* Latest payload sender, so the readiness retry below always posts the
     current store without being torn down and rescheduled on every edit. */
  const sendMessageRef = useRef<() => void>(() => {});
  const readinessIntervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const iframe = iframeRef.current;

    const sendMessage = () => {
      const messagePayload = {
        type: IFRAMEMessageTypes.CheckOutPreviewData,
        action: action,
        checkoutStore: checkoutStore,
      };
      iframe?.contentWindow?.postMessage(JSON.stringify(messagePayload), "*");
    };
    sendMessageRef.current = sendMessage;

    iframe?.removeEventListener("load", sendMessage);
    iframe?.addEventListener("load", sendMessage);
    window.addEventListener("message", handleMessageDataReceived);

    // The preview only reveals the iframe once it has acked a payload, and a
    // payload posted before the preview app has attached its listener is
    // dropped, so keep retrying until it acks. Retrying (rather than a single
    // delayed post that every builder edit cancelled and rescheduled) is what
    // keeps a run of edits from postponing the first payload indefinitely and
    // leaving the preview stuck on its loader. Once acked we know it is
    // listening, so later edits are pushed straight away.
    if (iframe?.contentWindow) {
      if (messagePostedRecieved) {
        sendMessage();
      } else if (!readinessIntervalRef.current) {
        let attemptsLeft = 20;
        readinessIntervalRef.current = setInterval(() => {
          if (attemptsLeft-- <= 0) {
            clearInterval(readinessIntervalRef.current);
            readinessIntervalRef.current = undefined;
            console.error(
              "Checkout preview never acknowledged its data; it is most likely failing to render. Check the preview frame's console.",
            );
            return;
          }
          sendMessageRef.current();
        }, 1000);
      }
    }

    return () => {
      iframe?.removeEventListener("load", sendMessage);
      window.removeEventListener("message", handleMessageDataReceived);
    };
  }, [
    action,
    checkoutStore,
    // detailPage carries the selected merchant account (and therefore the
    // surcharge percentage and card enablement) plus the seller shown in the
    // terms text. It is backfilled asynchronously once the merchant account
    // list loads, so without this the preview can keep rendering the stale
    // pre-backfill copy.
    checkoutStore.detailPage,
    checkoutStore.sellPage,
    checkoutStore.paymentPageData,
    checkoutStore.afterPayPage,
    checkoutStore.optionsPage,
    checkoutStore.donationdetail,
    checkoutStore.donationPricingData,
    messagePostedRecieved,
  ]);

  /* Stop retrying as soon as the preview acks, and never leave a timer behind. */
  useEffect(() => {
    if (messagePostedRecieved && readinessIntervalRef.current) {
      clearInterval(readinessIntervalRef.current);
      readinessIntervalRef.current = undefined;
    }
  }, [messagePostedRecieved]);

  useEffect(
    () => () => {
      if (readinessIntervalRef.current) {
        clearInterval(readinessIntervalRef.current);
        readinessIntervalRef.current = undefined;
      }
    },
    [],
  );

  function handleMessageDataReceived(event: any) {
    if (!event?.data || typeof event?.data !== "string") return;
    try {
      const data = JSON.parse(event?.data);
      if (data?.type !== IFRAMEMessageTypes.PreviewMessageDataReceived) return;
      if (iframeRef?.current) {
        iframeRef.current.style.display = "block";
        setMessagePostedRecieved(true);
      }
    } catch (e) {}
  }

  useEffect(() => {
    function handleResize() {
      // const zoomLevel = (window.innerWidth / window.outerWidth) * 100;
      // setZoom(zoomLevel);
      // setHeight(window.innerHeight);
      setWidth(window.innerWidth);
      setPreviewMode(window.innerWidth > 1900 ? "desktop" : "mobile");
    }

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const togglePreviewMode = (mode: PreviewMode) => {
    setPreviewMode(mode);
  };

  return (
    <CheckoutIframeWrapper className="float-block">
      {checkoutStore?.activeStep === UpsertCheckoutActiveStep.Sell &&
      checkoutStore.sellPage.items.length === 0 ? (
        <FlexContainer
          fxLayout="column"
          alignItems="center"
          justifyContent="center"
        >
          <CustomPlaceholders
            image={placeholderIcon}
            label={
              "When you add items, your checkout preview will be displayed here."
            }
            className="basic-plh basic-plh-height"
          />
          <CustomLabel fontSize={"20px"} fontWeight={"600"}></CustomLabel>
        </FlexContainer>
      ) : (
        <FlexContainer fxLayout="column" alignItems="center" spacing={30}>
          <div>
            <NavPills
              defaultActiveKey={previewMode}
              activeKey={previewMode}
              pills={[
                {
                  key: "desktop",
                  title: "DESKTOP",
                  isDisabled: window.innerWidth < 1900 ? true : false,
                },
                {
                  key: "mobile",
                  title: "MOBILE",
                },
              ]}
              onSelect={(key) => togglePreviewMode(key as PreviewMode)}
            />
          </div>
          <IFrameWrapper
            maxWidth={`${previewMode === "mobile" ? "650px" : "100%"} `}
          >
            {" "}
            {!messagePostedRecieved && new Loader(true).loaderComponent}
            <iframe
              src="/app/checkout/preview"
              style={{ display: "none" }}
              ref={iframeRef}
              title="checkout-preview"
              className={`${width > 1900 ? "desktop" : previewMode === "mobile" ? "mobile" : "desktop"}`}
              height={"100%"}
              // style={{
              //     transform: `scale(${zoom / 100})`,
              //     transformOrigin: 'top left',
              // }}
              // style={{
              //     height: 1200 * (zoom / 100) - 32, // Subtract header height
              //     transform: `scale(${zoom / 100})`,
              //     transformOrigin: 'top left',
              // }}
            />
          </IFrameWrapper>
        </FlexContainer>
      )}
    </CheckoutIframeWrapper>
  );
};

export default observer(CheckoutIFrame);
